import React, { useEffect, useState } from 'react'
import { IFile, IReport } from '../../helpers/Interfaces'
import { createMediaDocument, getDocumentMedia } from '../../helpers/FirebaseFunctions'
import Loading from '../Loading/Loading'
import FileUploadUI from '../FileUploadUI/FileUploadUI'
import DocumentButton from '../DocumentButton/DocumentButton'

type UploadedFilesProps = {
    readonly selectedDocument: IReport;
    readonly setShowAlert: CallableFunction;
    readonly setLoading: CallableFunction;
    readonly loading: boolean;
    readonly showAlert: any;
}

function CompletedUploadedFiles(props: UploadedFilesProps) {
    const [media, setMedia] = useState([] as IFile[]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getMedia(props.selectedDocument.mediaId)
    }, [props.selectedDocument.mediaId])

    function handleMediaInit(docMedia: IFile[]) {
        setMedia(docMedia);
    }

    async function getMedia(mediaId: string) {
        setLoading(true);
        await getDocumentMedia(mediaId, handleMediaInit)
        setLoading(false);
    }

    function addMedia(file: IFile) {
        props.setLoading(true);

        const ext = file.fileName.toLowerCase();
        const isAllowed = ext.endsWith("pdf") || ext.endsWith("doc") || ext.endsWith("docx");

        if (!isAllowed) {
            props.setShowAlert({
                isError: true,
                message: "Only PDF or Word (DOC/DOCX) files are allowed",
                open: true,
            });
            props.setLoading(false);
            return;
        }

        const isDuplicate = media.some((existing) => existing.fileId === file.fileId);

        if (!isDuplicate) {
            setMedia((prev: IFile[]) => [...prev, file]);
        }

        props.setLoading(false);
    }

    function removeMedia(fileId: string) {
        const selectedFile = media.find(file => file.fileId === fileId);

        if (!selectedFile) {
            props.setShowAlert({ isError: true, message: "File not Found", open: true });
            return;
        }

        if (selectedFile.duration > 0) {
            props.setShowAlert({ isError: true, message: "Cannot Delete Audio Files", open: true });
            return;
        }

        setMedia((prev: IFile[]) => prev.filter((file) => file.fileId !== fileId));
    }

    function removeMultipleMedia(fileIds: string[]) {
        const targetFiles = media.filter((file) => fileIds.includes(file.fileId));

        if (targetFiles.length !== fileIds.length) {
            props.setShowAlert({
                isError: true,
                message: "One or more files were not found",
                open: true,
            });
            return;
        }

        const hasAudioFile = targetFiles.some((file) => file.duration > 0);

        if (hasAudioFile) {
            props.setShowAlert({
                isError: true,
                message: "Cannot Delete Audio Files",
                open: true,
            });
            return;
        }

        const idsToRemoveSet = new Set(fileIds);
        setMedia((prev: IFile[]) => prev.filter((file) => !idsToRemoveSet.has(file.fileId)));
    }

    async function upload() {
        props.setLoading(true);

        let mediaAdded = await createMediaDocument(props.selectedDocument.mediaId, media);

        if (!mediaAdded) {
            let newShowAlert = props.showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
            props.setLoading(false);
            return;
        }

        let newShowAlert = props.showAlert;
        newShowAlert.isError = false;
        newShowAlert.message = "Changes Saved";
        newShowAlert.open = true;
        props.setShowAlert((prevDetails: any) => ({ ...prevDetails, ...newShowAlert }));
        props.setLoading(false);
    }

    function uploadedFilesView() {
        if (loading) {
            return (<Loading color='black' />)
        }

        return (<div className="uploaded-files-content">
            <FileUploadUI removeFile={removeMedia} setLoading={props.setLoading} setMedia={addMedia} media={media}
                setShowAlert={props.setShowAlert} showDelete={true} showMultiSelectDelete={true}
                removeMulitpleFiles={removeMultipleMedia} />
            <DocumentButton width={100} text={"Save"} onClick={upload} loading={props.loading} />
        </div>)
    }

    return (
        <div className='view-document-body uploaded-files'>
            {uploadedFilesView()}
        </div>
    )
}

export default CompletedUploadedFiles